import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { siteData, NavPage, CustomSection } from "@/store/siteData";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";

// ── Section Renderers ─────────────────────────────────────────────────────────

function HeroSection({ section }: { section: Extract<CustomSection, { type: 'hero' }> }) {
  return (
    <div className="relative min-h-[420px] md:min-h-[560px] flex items-center justify-center overflow-hidden">
      {section.imageUrl && (
        <>
          <img src={section.imageUrl} alt={section.title}
            className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0" style={{ background: `rgba(5,10,20,${section.overlayOpacity})` }}></div>
        </>
      )}
      {!section.imageUrl && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFFFFF] via-[#F8FAFC] to-[#F1F5F9]"></div>
      )}
      <div className="relative z-10 text-center max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-black text-[#0F172A] leading-tight mb-4">
          {section.title}
        </h1>
        {section.subtitle && (
          <p className="text-lg text-[#0F172A]/70 leading-relaxed">{section.subtitle}</p>
        )}
      </div>
    </div>
  );
}

function TextSection({ section }: { section: Extract<CustomSection, { type: 'text' }> }) {
  const alignClass = section.align === 'center' ? 'text-center mx-auto' : section.align === 'right' ? 'text-right ml-auto' : 'text-left';
  return (
    <section className="py-14 md:py-20 bg-white">
      <div className={`max-w-3xl px-6 md:px-8 ${alignClass}`}>
        {section.heading && (
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{section.heading}</h2>
        )}
        <p className="text-gray-600 leading-relaxed text-base md:text-lg whitespace-pre-line">{section.body}</p>
      </div>
    </section>
  );
}

function FeaturesSection({ section }: { section: Extract<CustomSection, { type: 'features' }> }) {
  return (
    <section className="py-14 md:py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        {section.heading && (
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12">{section.heading}</h2>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {section.items.map((item, i) => (
            <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gray-100 mb-4">
                <i className={`${item.icon} text-2xl text-gray-700`}></i>
              </div>
              <h3 className="text-gray-900 font-semibold text-base mb-2">{item.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection({ section }: { section: Extract<CustomSection, { type: 'stats' }> }) {
  return (
    <section className="py-14 md:py-20 bg-[#FFFFFF]">
      <div className="max-w-5xl mx-auto px-6 md:px-8">
        {section.heading && (
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] text-center mb-12">{section.heading}</h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {section.items.map((item, i) => (
            <div key={i} className="text-center bg-white/5 border border-white/10 rounded-xl py-8 px-4">
              <p className="text-3xl md:text-4xl font-black text-[#0F172A] mb-2">{item.value}</p>
              <p className="text-[#0F172A]/50 text-sm">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection({ section }: { section: Extract<CustomSection, { type: 'cta' }> }) {
  return (
    <section className="py-14 md:py-20 bg-gradient-to-br from-[#F8FAFC] to-[#1a2744]">
      <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">{section.heading}</h2>
        {section.subtitle && <p className="text-[#0F172A]/60 text-base mb-8">{section.subtitle}</p>}
        {section.buttonText && (
          <Link to={section.buttonLink || '/'}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#FFFFFF] font-semibold rounded-xl hover:bg-white/90 transition-colors text-sm">
            {section.buttonText}
            <i className="ri-arrow-right-line"></i>
          </Link>
        )}
      </div>
    </section>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CustomPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<NavPage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    siteData.load().then(() => {
      const pages = siteData.getNavPages();
      const found = pages.find(p => p.slug === `/sayfa/${slug}`);
      setPage(found ?? null);
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <i className="ri-loader-4-line text-3xl text-[#3B82F6] animate-spin"></i>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-center px-6 py-20">
          <div>
            <h1 className="text-4xl font-black text-gray-900 mb-4">Sayfa Bulunamadı</h1>
            <p className="text-gray-500 mb-8">Aradığınız sayfa mevcut değil veya kaldırılmış.</p>
            <Link to="/" className="px-6 py-3 bg-gray-900 text-[#0F172A] rounded-xl hover:bg-gray-700 transition-colors">
              Ana Sayfaya Dön
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const sections = page.sections ?? [];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <ScrollToTop />
      <Navbar />
      <main className="flex-1">
        {sections.length === 0 ? (
          <div className="flex items-center justify-center py-24 text-center px-6">
            <div>
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gray-100 mx-auto mb-4">
                <i className="ri-file-text-line text-3xl text-gray-400"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{page.label}</h1>
              <p className="text-gray-400 text-sm">Bu sayfanın içeriği henüz eklenmemiş.</p>
            </div>
          </div>
        ) : (
          sections.map(sec => (
            <div key={sec.id}>
              {sec.type === 'hero'     && <HeroSection section={sec} />}
              {sec.type === 'text'     && <TextSection section={sec} />}
              {sec.type === 'features' && <FeaturesSection section={sec} />}
              {sec.type === 'stats'    && <StatsSection section={sec} />}
              {sec.type === 'cta'      && <CTASection section={sec} />}
            </div>
          ))
        )}
      </main>
      <Footer />
    </div>
  );
}
